import React, { Component } from 'react'
import { Skeleton, Typography, Row, Col, Space, Form, Input, Select, Button, Collapse, Modal, message } from 'antd'
import { CaretRightOutlined, MinusCircleOutlined, CopyOutlined } from '@ant-design/icons'
import ReactHtmlParser from 'react-html-parser'
import MailContainer from '../../../MailContainer'
import DynamicFields from '../../../DynamicFields'
import ISmartResponseStruct from '../../../../interfaces/ISmartResponseStruct'
import { IDynamicBaseStruct } from '../../../../interfaces/IDynamicFields'
import { SmartResponseConstants } from '../../../../helpers/enums/SmartResponse'
import { EmailTemplatePreview } from '../../../../services/api/interfaces/ApiRequest'

type EmailTemplateStructProps = {
  templateUuid: string,
  uuid: string | null,
  loading: boolean,

  language_id?: number,
  parent_id?: number,
  preview?: any | null,

  data: ISmartResponseStruct,
  onSave?: (uuid: string | null, data: ISmartResponseStruct, language_id?: number, parent_id?: number) => void
  onChange?: (uuid: string) => void,

  loadPreview: (args: EmailTemplatePreview) => void
}

export default class EmailTemplateStruct extends Component<EmailTemplateStructProps> {

  state = {
    previewModal: false,
    data: EmailTemplateStruct.initialData(this.props.data)
  }

  form: any

  protected static initialData = (data: ISmartResponseStruct): ISmartResponseStruct => {
    const initial: ISmartResponseStruct = {
      model: {},
      values: {
        title: '',
        subject: '',
        preheader: '',
        main_picture: '',
        main_description: '',
        boxes: [],
        cards: [],
        main_buttons: [],
        final_buttons: []
      }
    }

    return {
      model: {
        ...initial.model,
        ...data.model
      },
      values: {
        ...initial.values,
        ...data.values
      }
    }
  }

  private parseModelToArray = (model: Record<any, any>) => {
    const result: Array<Record<any, any>> = []
    Object.keys(model).forEach(k => {
      result.push({
        key: k,
        value: model[k]
      })
    })

    return result
  }

  private parseModelToObject = (values: Array<Record<any, any>>) => {
    let result: Record<any, any> = {}

    values.forEach(model => {
      result[model.key] = model.value
    })

    return result
  }

  private getFormParsedFields = () => {
    const { data } = this.state

    let formValues = this.form.getFieldsValue(true)
    formValues = {
      ...formValues,
      model: this.parseModelToObject(formValues.model)
    }

    // Fix collapsed fields
    SmartResponseConstants.COLLAPSED_FIELDS.forEach(field => {
      if (formValues.values[field.key] === undefined && data.values[field.key]) {
        formValues.values[field.key] = data.values[field.key]
      }
    })

    return formValues
  }

  handlePreviewClose = () => {
    this.setState({
      previewModal: false
    })
  }

  handleFormChange = (changedValue, allValues) => {
    const { uuid, onChange } = this.props
    const { data } = this.state
    
    if (data.model && allValues.model) {
      data.model = {}

      // Update model values
      allValues.model.filter(model => model?.key && model?.value).forEach(model => {
        data.model[model.key] = model.value
      })

      this.setState({
        data
      })
    }

    // Dispatch on change
    if (onChange && uuid && this.form.isFieldsTouched([], true)) {
      onChange(uuid)
    }
  }

  handleFieldCopy = (name: string | number) => {
    if (!navigator.clipboard) {
      message.error('Failed to copy')
      return
    }

    const value = this.form.getFieldValue(['model', name])
    
    navigator.clipboard.writeText(`{{${value.key}}}`)
    message.success('Copied to clipboard')
  }

  handlePreview = () => {
    const { templateUuid, loadPreview } = this.props

    this.setState({
      previewModal: true
    }, () => {
      loadPreview({
        email_template: {
          uuid: templateUuid,
          custom: this.getFormParsedFields()
        }
      })
    })
  }

  handleSave = () => {
    const { uuid, language_id, parent_id, onSave } = this.props
    const { data } = this.state

    let formValues = this.getFormParsedFields()

    // Fix collapsed fields
    SmartResponseConstants.COLLAPSED_FIELDS.forEach(field => {
      if (formValues.values[field.key] === undefined && data.values[field.key]) {
        formValues.values[field.key] = data.values[field.key]
      }
    })

    this.setState({
      previewModal: false
    }, () => {
      if (onSave) {
        onSave(uuid, formValues, language_id, parent_id)
      }
    })
  }

  renderModels = () => {
    return (
      <Form.List
        name="model"
      >
        {(fields, { add, remove }) => (
          <>
            {
              fields.map((field, index) => (
                <Space key={`model-${index}`} style={{ display: 'flex', marginBottom: 8 }} size="large" align="start">
                  <Form.Item
                    {...field}
                    name={[field.name, 'key']}
                    rules={[
                      {
                        required: true,
                        message: 'Model key is required'
                      }
                    ]}
                    style={{width: '100%'}}
                  >
                    <Input placeholder="Model key" addonAfter={<CopyOutlined onClick={() => this.handleFieldCopy(field.name)} />} readOnly />
                  </Form.Item>

                  <Form.Item
                    {...field}
                    name={[field.name, 'value']}
                    rules={[
                      {
                        required: true,
                        message: 'Model value is required'
                      }
                    ]}
                  >
                    <Input placeholder="Model value" />
                  </Form.Item>

                  <MinusCircleOutlined hidden={true} onClick={() => remove(field.name)} />
                </Space>
              ))
            }

            <Form.Item hidden={true}>
              <Button type="dashed" onClick={() => add()}>
                Add model
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    )
  }

  renderModelSelect = (itemKey: string | number) => {
    const { data } = this.state

    return (
      <Select
        allowClear={true}
      >
        {
          Object.keys(data.model).map((k, index) => {
            return (
              <Select.Option key={`model-select-${itemKey}-${index}`} value={`{{${k}}}`}>
                {`{{${k}}}`}
              </Select.Option>
            )
          })
        }
      </Select>
    )
  }

  renderMainValues = () => {
    return (
      <>
        <Form.Item
          label="Title"
          name={['values', 'title']}
          rules={[
            {
              required: true,
              message: `Title is required`
            }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Subject"
          name={['values', 'subject']}
          rules={[
            {
              required: true,
              message: `Subject is required`
            }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Pre-Header"
          name={['values', 'preheader']}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Main Description"
          name={['values', 'main_description']}
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item
          label="Main Picture"
          name={['values', 'main_picture']}
        >
          {this.renderModelSelect('main_picture')}
        </Form.Item>
      </>
    )
  }

  renderCollapsableValues = () => {
    const CUSTOM_BASE_STRUCT: IDynamicBaseStruct = {
      ...SmartResponseConstants.BASE_STRUCT
    }

    // Boxes select
    CUSTOM_BASE_STRUCT.boxes.fields.picture.render = this.renderModelSelect
    if (CUSTOM_BASE_STRUCT.boxes.fields.labels.fields) {
      CUSTOM_BASE_STRUCT.boxes.fields.labels.fields.value.render = this.renderModelSelect
    }

    // Cards select
    CUSTOM_BASE_STRUCT.cards.fields.value.render = this.renderModelSelect

    // Main Buttons select
    CUSTOM_BASE_STRUCT.main_buttons.fields.link.render = this.renderModelSelect

    // Main Buttons select
    CUSTOM_BASE_STRUCT.final_buttons.fields.link.render = this.renderModelSelect

    return (
      <Collapse
        bordered={false}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
        style={{marginTop: 25}}
      >
        {
          SmartResponseConstants.COLLAPSED_FIELDS.map(field => (
            <Collapse.Panel header={field.title} key={field.key}>
              <DynamicFields name={['values', field.key]} baseStruct={CUSTOM_BASE_STRUCT} />
            </Collapse.Panel>
          ))
        }
      </Collapse>
    )
  }

  renderPreview = () => {
    const { preview } = this.props

    if (!preview) {
      return <Skeleton />
    }

    return (
      <MailContainer>
        {ReactHtmlParser(preview.content)}
      </MailContainer>
    )
  }

  render() {
    const { data, loading } = this.props
    const { previewModal } = this.state

    const values = {
      ...data || {},
      model: this.parseModelToArray(data.model || {})
    }

    return (
      <>
        <Form
          name="email_template_struct"
          initialValues={values}
          layout="vertical"
          ref={ref => this.form = ref}
          onFinish={this.handleSave}
          onValuesChange={this.handleFormChange}
        >
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Typography.Title level={5}>Models</Typography.Title>

              {this.renderModels()}
            </Col>

            <Col xs={24} md={12}>
              <Typography.Title level={5}>Values</Typography.Title>

              {this.renderMainValues()}
            </Col>
          </Row>

          {this.renderCollapsableValues()}

          <Form.Item style={{marginTop: 32}}>
            <Button type="primary" size="large" loading={loading} onClick={this.handlePreview} block>
              Preview
            </Button>
          </Form.Item>
        </Form>

        <Modal
          okText="Save"
          closable={false}
          width={800}
          visible={previewModal}
          onCancel={this.handlePreviewClose}
          onOk={this.handleSave}
        >
          {this.renderPreview()}
        </Modal>
      </>
    )
  }
}
